import React from 'react';
import { Mail, Github, Linkedin, MessageSquare, Zap, Palette, Facebook, ShieldCheck, Sparkles, Phone } from 'lucide-react';
import { MTLogo } from './Logo';
import { useLanguage } from '../context/LanguageContext';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const { t, isUrdu } = useLanguage();

  const footerLinks = [
    { name: t.nav.home, href: '#home' },
    { name: t.nav.about, href: '#about' },
    { name: t.nav.skills, href: '#expertise' },
    { name: t.nav.services, href: '#services' },
    { name: t.nav.portfolio, href: '#portfolio' },
    { name: t.nav.testimonials, href: '#testimonials' },
    { name: t.nav.contact, href: '#contact' },
  ];

  return (
    <footer className="relative bg-black text-white pt-16 sm:pt-20 pb-12 overflow-hidden border-t border-slate-800 w-full z-0">
      {/* Background Video - Crystal Clear 100% Opacity */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none opacity-100"
      >
        <source src="/Footer-video.mp4.mp4" type="video/mp4" />
      </video>

      {/* Subtle Gradient Shadow At Top & Bottom for readability without obstructing video */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-black/30 to-slate-950/80 z-0 pointer-events-none" />

      {/* Top Border Gradient Accent spanning full edge-to-edge */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-indigo-600 via-sky-500 to-indigo-500 z-10" />

      <div className="w-full px-6 sm:px-12 lg:px-16 xl:px-24 relative z-10">
        
        {/* Main Footer Full Width Content Grid */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start relative z-10 mb-12">
          
          {/* Branding Column (Col 5) */}
          <div className="lg:col-span-5 space-y-5">
            <a href="#home" className="inline-flex items-center gap-3.5 hover:opacity-90 transition-opacity group">
               <div className="group-hover:scale-105 transition-transform">
                  <MTLogo className="w-11 h-11 shadow-lg shadow-indigo-500/30" />
               </div>
               <div>
                 <div className="text-2xl sm:text-3xl font-display font-extrabold tracking-tight text-white">
                    M.<span className="text-indigo-400">TAIBE</span>
                 </div>
                 <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-indigo-300 block -mt-1">
                   {isUrdu ? 'ویب آرکیٹیکٹ اور میڈیا تخلیق کار' : 'Web Architect & Media Creator'}
                 </span>
               </div>
            </a>

            <p className="text-slate-300 text-xs sm:text-sm font-normal leading-relaxed max-w-lg">
              {isUrdu 
                ? 'کسٹم ورڈپریس تھیمز، ری ایکٹ ویب ایپلیکیشنز، اور وائرل AI ویڈیو ایڈیٹنگ میں مہارت۔ موثر ڈیجیٹل سلوشنز تیار کرنے کے لیے پرعزم۔'
                : 'Specialized in custom WordPress themes, React web applications, and viral AI video media editing. Dedicated to engineering scalable, high-converting digital solutions.'}
            </p>

            {/* Direct Quick Contact Info */}
            <div className="flex flex-wrap items-center gap-3 pt-2 text-xs font-medium text-slate-300">
              <a href="mailto:taibejaved485@gmail.com" className="inline-flex items-center gap-2 hover:text-indigo-400 transition-colors bg-slate-900/80 border border-slate-700/80 px-3.5 py-2 rounded-xl backdrop-blur-md shadow-sm">
                <Mail size={14} className="text-indigo-400" />
                <span>taibejaved485@gmail.com</span>
              </a>
              <a href="https://wa.me/923706487654" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-emerald-400 transition-colors bg-slate-900/80 border border-slate-700/80 px-3.5 py-2 rounded-xl backdrop-blur-md shadow-sm">
                <Phone size={14} className="text-emerald-400" />
                <span>+92 370 6487654</span>
              </a>
            </div>
          </div>

          {/* Navigation & Socials (Col 4) */}
          <div className="lg:col-span-4 grid grid-cols-2 gap-8 sm:gap-12">
            
            {/* Navigation Links */}
            <div className="space-y-4">
              <span className="text-white text-xs font-extrabold uppercase tracking-widest block border-b border-slate-800 pb-2">
                {isUrdu ? 'نیویگیشن' : 'Navigation'}
              </span>
              <div className="flex flex-col gap-2.5">
                {footerLinks.map(link => (
                  <a key={link.name} href={link.href} className="text-xs font-semibold text-slate-300 hover:text-indigo-400 uppercase tracking-wider transition-colors flex items-center gap-1.5 group">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-600 group-hover:bg-indigo-400 transition-colors" />
                    {link.name}
                  </a>
                ))}
              </div>
            </div>

            {/* Social Channels */}
            <div className="space-y-4">
              <span className="text-white text-xs font-extrabold uppercase tracking-widest block border-b border-slate-800 pb-2">
                {isUrdu ? 'سوشل میڈیا' : 'Social Media'}
              </span>
              <div className="flex flex-col gap-2.5">
                {[
                  { icon: Github, href: 'https://github.com/taibejaved485-afk', label: 'GitHub' },
                  { icon: Linkedin, href: 'https://www.linkedin.com/in/taibe-javed-b28283396/', label: 'LinkedIn' },
                  { icon: Facebook, href: 'https://www.facebook.com/share/1cL7nVbZad/', label: 'Facebook' },
                  { icon: Palette, href: 'https://www.behance.net/Taibegraphics', label: 'Behance' },
                  { icon: MessageSquare, href: 'https://wa.me/923706487654', label: 'WhatsApp' }
                ].map((item, i) => (
                  <a key={i} href={item.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 text-xs font-semibold text-slate-300 hover:text-indigo-400 transition-colors group">
                    <div className="w-7 h-7 rounded-lg bg-slate-900/90 border border-slate-700/80 flex items-center justify-center text-slate-300 group-hover:bg-indigo-600 group-hover:text-white group-hover:border-indigo-500 transition-all shadow-sm">
                      <item.icon size={14} aria-hidden="true" />
                    </div>
                    <span>{item.label}</span>
                  </a>
                ))}
              </div>
            </div>

          </div>

          {/* Availability & Status Panel (Col 3) */}
          <div className="lg:col-span-3 space-y-4">
            <div className="bg-slate-900/85 backdrop-blur-md border border-slate-800 p-5 rounded-2xl space-y-3.5 shadow-lg">
               <div className="flex items-center justify-between">
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-400 flex items-center gap-2">
                     <Zap size={14} className="animate-pulse text-emerald-400" /> 
                     {isUrdu ? 'پروجیکٹس کے لیے دستیاب' : 'Available for Projects'}
                  </span>
                  <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-800 border border-slate-700 px-2 py-0.5 rounded-md">v4.5</span>
               </div>

               <p className="text-[11px] text-slate-300 leading-snug">
                 {isUrdu 
                   ? 'کسٹم ورڈپریس تھیمز، مکمل ری ایکٹ سسٹمز، اور ماہانہ سوشل میڈیا ویڈیو ایڈیٹنگ کے لیے دستیاب۔'
                   : 'Accepting custom WordPress themes, full React builds, and monthly social media video editing retainers.'}
               </p>

               <div className="flex items-center gap-2 pt-2 border-t border-slate-800 text-[10px] font-bold text-slate-300">
                 <ShieldCheck size={14} className="text-indigo-400" />
                 <span>{isUrdu ? 'جواب کا اوسط وقت: < 2 گھنٹے' : 'Avg. Response Time: < 2 Hours'}</span>
               </div>
            </div>
          </div>

        </div>

        {/* Bottom Legal Bar - Full Width Divider */}
        <div className="border-t border-slate-800/80 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-slate-400 text-xs font-medium">
          <div className="flex items-center gap-2">
            <span>© {currentYear} <strong className="text-white">M. Taibe</strong>. {isUrdu ? 'جملہ حقوق محفوظ ہیں۔' : 'All rights reserved.'}</span>
          </div>
          <div className="flex items-center gap-4 text-[11px]">
            <span className="inline-flex items-center gap-1.5 text-slate-300">
              <Sparkles size={12} className="text-indigo-400" />
              {isUrdu ? 'بہترین مہارت اور کوڈ سے تیار کردہ' : 'Crafted with Precision & Code'}
            </span>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;

